// src/components/Footer.jsx
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin } from "lucide-react";

export default function Footer() {
  const [offsetY, setOffsetY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setOffsetY(window.scrollY * 0.1);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <footer className="relative bg-gray-900 text-white overflow-hidden">
      {/* Parallax background (reuse testimonials background) */}
      <motion.div
        className="absolute inset-0"
        style={{ transform: `translateY(${offsetY}px)` }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20 pointer-events-none"
          style={{ backgroundImage: "url('/testimonials-bg.jpg')" }}
        />
      </motion.div>

      {/* Top diagonal divider */}
      <div className="absolute top-0 left-0 w-full overflow-hidden leading-[0] rotate-180">
        <svg
          className="relative block w-[calc(100%+1.3px)] h-20 text-white"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          viewBox="0 0 1200 120"
        >
          {/* Use a dark fill so the divider blends into the footer */}
          <path d="M1200 0L0 0 892.25 114.72 1200 0z" fill="#0b1220" />
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-4 gap-12">
        {/* Logo & Description */}
        <div>
          <img src="/logo.png" alt="Aspen Plush Homes" className="h-10 mb-6" />
          <p className="text-gray-300 text-sm leading-relaxed">
            Aspen Plush Homes delivers premium, innovative, and timeless living
            spaces that redefine luxury and comfort.
          </p>
        </div>

        {/* Quick Links */}
        <div>
          <h3 className="font-semibold text-lg mb-4">Quick Links</h3>
          <ul className="space-y-2 text-gray-300 text-sm">
            <li><Link to="/" className="hover:text-white">Home</Link></li>
            <li><Link to="/about" className="hover:text-white">About</Link></li>
            <li><Link to="/projects" className="hover:text-white">Projects</Link></li>
            <li><Link to="/services" className="hover:text-white">Services</Link></li>
            <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
          </ul>
        </div>

        {/* Services */}
        <div>
          <h3 className="font-semibold text-lg mb-4">Our Services</h3>
          <ul className="space-y-2 text-gray-300 text-sm">
            <li>Architectural Design</li>
            <li>Construction</li>
            <li>Interior Design</li>
            <li>Smart Homes</li>
            <li>Consulting</li>
            <li>Building & Construction</li>
          </ul>
        </div>

        {/* Contact Info */}
        <div>
          <h3 className="font-semibold text-lg mb-4">Contact Us</h3>
          <ul className="space-y-3 text-gray-300 text-sm">
            <li>Email: aspenplushhomes@gmail.com</li>
            <li>Phone: +233277348176</li>
            <li>Location: Aspen Plush Homes, Accra-Taifa, Ghana</li>
          </ul>
          <div className="flex gap-4 mt-6">
            <a href="#" className="hover:text-blue-400" aria-label="Facebook"><Facebook /></a>
            <a href="#" className="hover:text-pink-400" aria-label="Instagram"><Instagram /></a>
            <a href="#" className="hover:text-blue-600" aria-label="LinkedIn"><Linkedin /></a>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="relative z-10 border-t border-white/10 text-center py-6 text-gray-400 text-sm">
        <div>© {new Date().getFullYear()} Aspen Plush Homes. All rights reserved.</div>
        <div className="mt-1">
          Developed by{" "}
          <a
            href="https://prolificwills.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 hover:text-blue-500"
          >
            Prolificwills
          </a>
        </div>
      </div>
    </footer>
  );
}
